import React, { useCallback, useEffect, useState } from "react";
import { Row, Col, Card, Statistic, Spin, Alert, List, Progress, Tag, Empty, Button } from "antd";
import {
    UserOutlined,
    UsergroupAddOutlined,
    CalendarOutlined,
    ClockCircleOutlined,
    ThunderboltOutlined,
    PercentageOutlined,
    ReloadOutlined,
} from "@ant-design/icons";
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    Tooltip,
    CartesianGrid,
    PieChart,
    Pie,
    Cell,
    BarChart,
    Bar,
} from "recharts";
import { api } from "../api";

const COLORS = ["#2563eb", "#16a34a", "#f97316", "#a855f7", "#ef4444", "#0ea5e9"];

const StatCard = ({ title, value, icon, color, suffix }) => (
    <Card variant="borderless" className="admin-stat-card">
        <div className="admin-stat-icon" style={{ background: color }}>
            {icon}
        </div>
        <Statistic title={title} value={value} suffix={suffix} />
    </Card>
);

const Dashboard = () => {
    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(true);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        api.analytics()
            .then(setData)
            .catch((e) => setError(e.message))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (loading && !data) return <Spin size="large" style={{ marginTop: 80 }} />;
    if (error) return <Alert type="error" showIcon message={error} />;

    const { cards, registrationTrend, eventsByStatus, topTeams, pendingApplicants, unverified } = data;

    const cardDefs = [
        { title: "Users", value: cards.totalUsers, icon: <UserOutlined />, color: "#2563eb" },
        { title: "Teams / Groups", value: cards.totalGroups, icon: <UsergroupAddOutlined />, color: "#16a34a" },
        { title: "Events", value: cards.totalEvents, icon: <CalendarOutlined />, color: "#f97316" },
        { title: "Total Volunteer Hours", value: Number(cards.totalHours || 0).toFixed(1), icon: <ClockCircleOutlined />, color: "#a855f7" },
        { title: "Active Volunteers (This Month)", value: cards.activeThisMonth, icon: <ThunderboltOutlined />, color: "#0ea5e9" },
        { title: "Event Fulfillment Rate", value: cards.fulfillmentRate, suffix: "%", icon: <PercentageOutlined />, color: "#ef4444" },
    ];

    const mailPct = unverified.total
        ? Math.round(((unverified.total - unverified.mailUnverified) / unverified.total) * 100)
        : 100;
    const profilePct = unverified.total
        ? Math.round(((unverified.total - unverified.profileIncomplete) / unverified.total) * 100)
        : 100;

    return (
        <div>
            <div className="admin-page-head">
                <h1 className="admin-page-title">Overview</h1>
                <Button icon={<ReloadOutlined />} loading={loading} onClick={load}>
                    Refresh
                </Button>
            </div>

            <Row gutter={[24, 24]}>
                {cardDefs.map((c) => (
                    <Col xs={24} sm={12} lg={8} key={c.title}>
                        <StatCard {...c} />
                    </Col>
                ))}
            </Row>

            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col xs={24} lg={14}>
                    <Card title="Volunteer Registration Trend" variant="borderless">
                        {registrationTrend.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <ResponsiveContainer width="100%" height={280}>
                                <LineChart data={registrationTrend} margin={{ left: -16 }}>
                                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                                    <XAxis dataKey="month" fontSize={12} />
                                    <YAxis allowDecimals={false} fontSize={12} />
                                    <Tooltip />
                                    <Line
                                        type="monotone"
                                        dataKey="count"
                                        name="New users"
                                        stroke="#2563eb"
                                        strokeWidth={2.5}
                                        dot={{ r: 3 }}
                                    />
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                </Col>
                <Col xs={24} lg={10}>
                    <Card title="Events Breakdown (by Status)" variant="borderless">
                        {eventsByStatus.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <ResponsiveContainer width="100%" height={280}>
                                <PieChart>
                                    <Pie
                                        data={eventsByStatus}
                                        dataKey="value"
                                        nameKey="name"
                                        innerRadius={60}
                                        outerRadius={100}
                                        paddingAngle={2}
                                        label={(e) => e.name}
                                    >
                                        {eventsByStatus.map((_, i) => (
                                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                                        ))}
                                    </Pie>
                                    <Tooltip />
                                </PieChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                </Col>
            </Row>

            <Row gutter={[24, 24]} style={{ marginTop: 24 }}>
                <Col xs={24} lg={12}>
                    <Card title="Top Teams (by event count)" variant="borderless">
                        {topTeams.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
                        ) : (
                            <ResponsiveContainer width="100%" height={280}>
                                <BarChart
                                    data={topTeams}
                                    layout="vertical"
                                    margin={{ left: 20 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke="#eef2f7" />
                                    <XAxis type="number" allowDecimals={false} fontSize={12} />
                                    <YAxis
                                        type="category"
                                        dataKey="name"
                                        width={110}
                                        fontSize={12}
                                    />
                                    <Tooltip />
                                    <Bar dataKey="eventCount" name="Events" fill="#16a34a" radius={[0, 6, 6, 0]} />
                                </BarChart>
                            </ResponsiveContainer>
                        )}
                    </Card>
                </Col>

                <Col xs={24} lg={12}>
                    <Card title="Pending Applicants" variant="borderless" style={{ marginBottom: 24 }}>
                        {pendingApplicants.length === 0 ? (
                            <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No pending applicants" />
                        ) : (
                            <List
                                size="small"
                                dataSource={pendingApplicants}
                                renderItem={(p) => (
                                    <List.Item
                                        actions={[<Tag color="orange" key="c">{p.count} pending</Tag>]}
                                    >
                                        {p.title}
                                    </List.Item>
                                )}
                            />
                        )}
                    </Card>

                    <Card title="Account Verification" variant="borderless">
                        <div style={{ marginBottom: 12 }}>
                            <span>Email verified</span>
                            <Progress percent={mailPct} status="active" />
                            <small style={{ color: "#64748b" }}>
                                {unverified.mailUnverified} of {unverified.total} unverified
                            </small>
                        </div>
                        <div>
                            <span>Profile complete</span>
                            <Progress percent={profilePct} strokeColor="#16a34a" />
                            <small style={{ color: "#64748b" }}>
                                {unverified.profileIncomplete} of {unverified.total} incomplete
                            </small>
                        </div>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default Dashboard;
